// src/components/payments/PaymentGateways.js
import React, { useState, useEffect } from 'react';
import { paymentsService } from '../../services/paymentsService';
import { usePageTitle } from '../../hooks/usePageTitle';
import './PaymentGateways.css';

const PaymentGateways = () => {
    usePageTitle('Payment Gateways');
    const [gateways, setGateways] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createForm, setCreateForm] = useState({
        name: '',
        code: '',
        type: 'card',
        country_codes: ['NG'],
        api_endpoint: '',
        public_key: '',
        secret_key: '',
        is_active: true,
        configuration: {}
    });
    const [createConfigInput, setCreateConfigInput] = useState('{}');
    const [editConfigInput, setEditConfigInput] = useState('{}');
    const [showCountryModal, setShowCountryModal] = useState(false);
    const [countryModalMode, setCountryModalMode] = useState('create'); // 'create' or 'edit'
    const [countrySearch, setCountrySearch] = useState('');
    const [newCountryCode, setNewCountryCode] = useState('');
    const [newCountryName, setNewCountryName] = useState('');
    const [customCountries, setCustomCountries] = useState([]);

    const bankTransferConfigTemplate = {
        bank_accounts_by_country: {
            DEFAULT: {
                bank_name: '',
                account_name: '',
                account_number: '',
                swift_code: '',
                bank_address: '',
                processing_time: '24 hours',
                additional_info: ''
            },
            PK: {
                bank_name: '',
                account_name: '',
                account_number: '',
                swift_code: '',
                bank_address: '',
                processing_time: '24 hours',
                additional_info: ''
            }
        }
    };

    const parseConfigurationInput = (rawInput, contextLabel) => {
        const trimmed = (rawInput || '').trim();
        if (!trimmed) return {};

        try {
            const parsed = JSON.parse(trimmed);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                throw new Error('Configuration must be a JSON object');
            }
            return parsed;
        } catch (err) {
            throw new Error(`${contextLabel} configuration JSON is invalid: ${err.message}`);
        }
    };

    const handleCreateGateway = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const parsedConfiguration = parseConfigurationInput(createConfigInput, 'Create gateway');
            await paymentsService.createPaymentGateway({
                ...createForm,
                configuration: parsedConfiguration
            });
            setSuccess('Gateway created successfully!');
            setShowCreateForm(false);
            setCreateForm({
                name: '',
                code: '',
                type: 'card',
                country_codes: ['NG'],
                api_endpoint: '',
                public_key: '',
                secret_key: '',
                is_active: true,
                configuration: {}
            });
            setCreateConfigInput('{}');
            await loadGateways();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message);
            console.error('Error creating gateway:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateCountryToggle = (countryCode) => {
        const currentCodes = createForm.country_codes || [];
        if (currentCodes.includes(countryCode)) {
            setCreateForm({
                ...createForm,
                country_codes: currentCodes.filter(c => c !== countryCode)
            });
        } else {
            setCreateForm({
                ...createForm,
                country_codes: [...currentCodes, countryCode]
            });
        }
    };

    const gatewayTypes = ['bank_transfer', 'card', 'mobile_money', 'digital_wallet', 'crypto'];

    const defaultCountries = [
        { code: 'NG', name: 'Nigeria' },
        { code: 'GH', name: 'Ghana' },
        { code: 'KE', name: 'Kenya' },
        { code: 'ZA', name: 'South Africa' },
        { code: 'EG', name: 'Egypt' },
        { code: 'US', name: 'United States' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'CA', name: 'Canada' },
        { code: 'IN', name: 'India' },
        { code: 'PK', name: 'Pakistan' },
        { code: 'AU', name: 'Australia' },
        { code: 'DE', name: 'Germany' },
        { code: 'FR', name: 'France' },
        { code: 'IT', name: 'Italy' },
        { code: 'ES', name: 'Spain' }
    ];

    // Merge default countries with custom countries
    const countries = [...defaultCountries, ...customCountries];

    // Filter countries based on search
    const filteredCountries = countries.filter(country =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        country.code.toLowerCase().includes(countrySearch.toLowerCase())
    );

    const openCountryModal = (mode) => {
        setCountryModalMode(mode);
        setShowCountryModal(true);
        setCountrySearch('');
    };

    const closeCountryModal = () => {
        setShowCountryModal(false);
        setCountrySearch('');
        setNewCountryCode('');
        setNewCountryName('');
    };

    const handleAddCustomCountry = () => {
        const code = newCountryCode.trim().toUpperCase();
        const name = newCountryName.trim();

        if (!code || !name) {
            alert('Please enter both country code and name');
            return;
        }

        if (code.length !== 2) {
            alert('Country code must be exactly 2 characters (ISO 3166-1 alpha-2)');
            return;
        }

        // Check if country already exists
        if (countries.find(c => c.code === code)) {
            alert(`Country with code ${code} already exists`);
            return;
        }

        // Add to custom countries
        const newCountry = { code, name };
        setCustomCountries([...customCountries, newCountry]);

        // Add to selected countries in the appropriate form
        if (countryModalMode === 'create') {
            handleCreateCountryToggle(code);
        } else {
            handleCountryToggle(code);
        }

        setNewCountryCode('');
        setNewCountryName('');
        setSuccess(`Country ${name} (${code}) added successfully!`);
        setTimeout(() => setSuccess(null), 3000);
    };

    useEffect(() => {
        loadGateways();
    }, []);

    const loadGateways = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await paymentsService.getPaymentGateways();
            setGateways(response.payment_gateways || []);
        } catch (err) {
            setError(err.message);
            console.error('Error loading gateways:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (gateway) => {
        const parsedConfiguration = typeof gateway.configuration === 'string'
            ? JSON.parse(gateway.configuration || '{}')
            : gateway.configuration || {};

        setEditingId(gateway.id);
        setEditForm({
            ...gateway,
            country_codes: Array.isArray(gateway.country_codes)
                ? gateway.country_codes
                : JSON.parse(gateway.country_codes || '[]'),
            configuration: parsedConfiguration
        });
        setEditConfigInput(JSON.stringify(parsedConfiguration, null, 2));
        setError(null);
        setSuccess(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({});
        setEditConfigInput('{}');
        setError(null);
    };

    const handleSave = async (gatewayId) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const parsedConfiguration = parseConfigurationInput(editConfigInput, 'Edit gateway');
            await paymentsService.updatePaymentGateway(gatewayId, {
                name: editForm.name,
                code: editForm.code,
                type: editForm.type,
                country_codes: editForm.country_codes,
                api_endpoint: editForm.api_endpoint || null,
                public_key: editForm.public_key || null,
                secret_key: editForm.secret_key || null,
                is_active: editForm.is_active ? 1 : 0,
                configuration: parsedConfiguration
            });

            setSuccess('Gateway updated successfully!');
            setEditingId(null);
            setEditForm({});
            setEditConfigInput('{}');
            await loadGateways();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message);
            console.error('Error updating gateway:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (gatewayId, gatewayName) => {
        if (!window.confirm(`Are you sure you want to delete ${gatewayName}?`)) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await paymentsService.deletePaymentGateway(gatewayId);
            setSuccess('Gateway deleted successfully!');
            await loadGateways();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message);
            console.error('Error deleting gateway:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (gateway) => {
        setLoading(true);
        setError(null);
        try {
            await paymentsService.updatePaymentGateway(gateway.id, {
                is_active: gateway.is_active ? 0 : 1
            });
            setSuccess(`Gateway ${gateway.is_active ? 'deactivated' : 'activated'} successfully!`);
            await loadGateways();

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCountryToggle = (countryCode) => {
        const currentCodes = editForm.country_codes || [];
        if (currentCodes.includes(countryCode)) {
            setEditForm({
                ...editForm,
                country_codes: currentCodes.filter(c => c !== countryCode)
            });
        } else {
            setEditForm({
                ...editForm,
                country_codes: [...currentCodes, countryCode]
            });
        }
    };

    const getCountryNames = (countryCodes) => {
        if (!countryCodes) return 'All';
        const codes = Array.isArray(countryCodes) ? countryCodes : JSON.parse(countryCodes || '[]');
        if (codes.includes('*')) return 'All Countries';
        return codes.map(code =>
            countries.find(c => c.code === code)?.name || code
        ).join(', ');
    };

    if (loading && !editingId) {
        return (
            <div className="payment-gateways">
                <div className="container">
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading payment gateways...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-gateways">
            <div className="container">
                <div className="page-header">
                    <div className="header-content">
                        <h1>Payment Gateways</h1>
                        <p>Manage payment gateways for your organization</p>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowCreateForm(!showCreateForm)}
                    >
                        <i className="fas fa-plus"></i>
                        {showCreateForm ? 'Cancel' : 'Add Gateway'}
                    </button>
                </div>

                {showCreateForm && (
                    <div className="create-gateway-form">
                        <h3>Create New Payment Gateway</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>Gateway Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm({...createForm, name: e.target.value})}
                                    placeholder="e.g., Paystack"
                                />
                            </div>

                            <div className="form-group">
                                <label>Gateway Code *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={createForm.code}
                                    onChange={(e) => setCreateForm({...createForm, code: e.target.value.toLowerCase()})}
                                    placeholder="e.g., paystack"
                                />
                            </div>

                            <div className="form-group">
                                <label>Type *</label>
                                <select
                                    className="form-input"
                                    value={createForm.type}
                                    onChange={(e) => setCreateForm({...createForm, type: e.target.value})}
                                >
                                    {gatewayTypes.map(type => (
                                        <option key={type} value={type}>
                                            {type.replace('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>API Endpoint</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={createForm.api_endpoint}
                                    onChange={(e) => setCreateForm({...createForm, api_endpoint: e.target.value})}
                                    placeholder="https://api.gateway.com"
                                />
                            </div>

                            <div className="form-group">
                                <label>Public Key</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={createForm.public_key}
                                    onChange={(e) => setCreateForm({...createForm, public_key: e.target.value})}
                                    placeholder="pk_..."
                                />
                            </div>

                            <div className="form-group">
                                <label>Secret Key</label>
                                <input
                                    type="password"
                                    className="form-input"
                                    value={createForm.secret_key}
                                    onChange={(e) => setCreateForm({...createForm, secret_key: e.target.value})}
                                    placeholder="sk_..."
                                />
                            </div>

                            <div className="form-group full-width">
                                <label>Supported Countries</label>
                                <button
                                    type="button"
                                    className="countries-btn"
                                    onClick={() => openCountryModal('create')}
                                    style={{ width: 'auto', minWidth: '200px' }}
                                >
                                    {createForm.country_codes?.length || 0} countries selected
                                </button>
                            </div>

                            <div className="form-group full-width">
                                <label>Configuration (JSON)</label>
                                <textarea
                                    className="form-input"
                                    rows={createForm.type === 'bank_transfer' ? 12 : 6}
                                    value={createConfigInput}
                                    onChange={(e) => setCreateConfigInput(e.target.value)}
                                    placeholder='{"bank_accounts_by_country":{"DEFAULT":{"bank_name":"","account_name":"","account_number":""}}}'
                                    style={{ fontFamily: 'monospace' }}
                                />
                                {createForm.type === 'bank_transfer' && (
                                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                        <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => setCreateConfigInput(JSON.stringify(bankTransferConfigTemplate, null, 2))}
                                        >
                                            Use Multi-Country Bank Template
                                        </button>
                                        <small style={{ color: '#64748b' }}>
                                            Add per-country accounts under <code>bank_accounts_by_country</code> (e.g. <code>PK</code>, <code>US</code>, <code>DEFAULT</code>).
                                        </small>
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <label className="toggle-label">
                                    <input
                                        type="checkbox"
                                        checked={createForm.is_active}
                                        onChange={(e) => setCreateForm({...createForm, is_active: e.target.checked})}
                                    />
                                    <span>Active</span>
                                </label>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowCreateForm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateGateway}
                                disabled={loading || !createForm.name || !createForm.code || !createForm.type}
                            >
                                <i className="fas fa-plus"></i>
                                Create Gateway
                            </button>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="alert alert-error">
                        <i className="fas fa-exclamation-circle"></i>
                        <span>{error}</span>
                        <button onClick={() => setError(null)} className="alert-close">×</button>
                    </div>
                )}

                {success && (
                    <div className="alert alert-success">
                        <i className="fas fa-check-circle"></i>
                        <span>{success}</span>
                        <button onClick={() => setSuccess(null)} className="alert-close">×</button>
                    </div>
                )}

                <div className="gateways-table-wrapper">
                    <table className="gateways-table">
                        <thead>
                            <tr>
                                <th>Status</th>
                                <th>Gateway Name</th>
                                <th>Code</th>
                                <th>Type</th>
                                <th>Countries</th>
                                <th>API Endpoint</th>
                                <th>Public Key</th>
                                <th>Secret Key</th>
                                <th>Configuration</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gateways.map(gateway => (
                                editingId === gateway.id ? (
                                    <tr key={gateway.id} className="editing-row">
                                        <td>
                                            <label className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={editForm.is_active}
                                                    onChange={(e) => setEditForm({
                                                        ...editForm,
                                                        is_active: e.target.checked
                                                    })}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editForm.name}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    name: e.target.value
                                                })}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editForm.code}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    code: e.target.value
                                                })}
                                            />
                                        </td>
                                        <td>
                                            <select
                                                className="form-input"
                                                value={editForm.type}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    type: e.target.value
                                                })}
                                            >
                                                {gatewayTypes.map(type => (
                                                    <option key={type} value={type}>
                                                        {type.replace('_', ' ')}
                                                    </option>
                                                ))}
                                            </select>
                                        </td>
                                        <td>
                                            <div className="countries-dropdown">
                                                <button
                                                    type="button"
                                                    className="countries-btn"
                                                    onClick={() => openCountryModal('edit')}
                                                >
                                                    {editForm.country_codes?.length || 0} selected
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editForm.api_endpoint || ''}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    api_endpoint: e.target.value
                                                })}
                                                placeholder="API URL"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={editForm.public_key || ''}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    public_key: e.target.value
                                                })}
                                                placeholder="Public Key"
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="password"
                                                className="form-input"
                                                value={editForm.secret_key || ''}
                                                onChange={(e) => setEditForm({
                                                    ...editForm,
                                                    secret_key: e.target.value
                                                })}
                                                placeholder="Secret Key"
                                            />
                                        </td>
                                        <td>
                                            <textarea
                                                className="form-input"
                                                rows={editForm.type === 'bank_transfer' ? 8 : 4}
                                                value={editConfigInput}
                                                onChange={(e) => setEditConfigInput(e.target.value)}
                                                placeholder="{}"
                                                style={{ fontFamily: 'monospace', minWidth: '280px' }}
                                            />
                                        </td>
                                        <td className="actions-cell">
                                            <button
                                                className="btn btn-success btn-sm"
                                                onClick={() => handleSave(gateway.id)}
                                                disabled={loading}
                                            >
                                                <i className="fas fa-check"></i>
                                            </button>
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={handleCancelEdit}
                                                disabled={loading}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ) : (
                                    <tr key={gateway.id}>
                                        <td>
                                            <button
                                                className={`status-badge ${gateway.is_active ? 'active' : 'inactive'}`}
                                                onClick={() => handleToggleActive(gateway)}
                                                title="Click to toggle status"
                                            >
                                                {gateway.is_active ? 'Active' : 'Inactive'}
                                            </button>
                                        </td>
                                        <td className="gateway-name">
                                            <strong>{gateway.name}</strong>
                                        </td>
                                        <td>
                                            <code className="code-badge">{gateway.code}</code>
                                        </td>
                                        <td>
                                            <span className="type-badge">
                                                {gateway.type.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="countries-cell">
                                            {getCountryNames(gateway.country_codes)}
                                        </td>
                                        <td className="api-cell">
                                            {gateway.api_endpoint ? (
                                                <code className="code-badge">{gateway.api_endpoint}</code>
                                            ) : (
                                                <span className="text-muted">Not set</span>
                                            )}
                                        </td>
                                        <td>
                                            {gateway.public_key ? (
                                                <span className="text-muted">••••••••</span>
                                            ) : (
                                                <span className="text-muted">Not set</span>
                                            )}
                                        </td>
                                        <td>
                                            {gateway.secret_key ? (
                                                <span className="text-muted">••••••••••••</span>
                                            ) : (
                                                <span className="text-muted">Not set</span>
                                            )}
                                        </td>
                                        <td>
                                            {gateway.type === 'bank_transfer' ? (
                                                (() => {
                                                    const config = typeof gateway.configuration === 'string'
                                                        ? JSON.parse(gateway.configuration || '{}')
                                                        : (gateway.configuration || {});
                                                    const accountCountries = Object.keys(config?.bank_accounts_by_country || {});
                                                    if (!accountCountries.length) {
                                                        return <span className="text-muted">No country mapping</span>;
                                                    }
                                                    return (
                                                        <span className="text-muted">
                                                            {accountCountries.slice(0, 4).join(', ')}
                                                            {accountCountries.length > 4 ? ` +${accountCountries.length - 4} more` : ''}
                                                        </span>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-muted">Configured</span>
                                            )}
                                        </td>
                                        <td className="actions-cell">
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleEdit(gateway)}
                                                disabled={loading}
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                className="btn btn-danger btn-sm"
                                                onClick={() => handleDelete(gateway.id, gateway.name)}
                                                disabled={loading}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                )
                            ))}
                        </tbody>
                    </table>

                    {gateways.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-credit-card"></i>
                            <h3>No Payment Gateways</h3>
                            <p>No payment gateways configured yet</p>
                        </div>
                    )}
                </div>

                {/* Country Selection Modal */}
                {showCountryModal && (
                    <div className="country-modal-overlay" onClick={closeCountryModal}>
                        <div className="country-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="country-modal-header">
                                <h3>Select Countries</h3>
                                <button className="country-modal-close" onClick={closeCountryModal}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className="country-modal-body">
                                <div className="country-search">
                                    <div style={{ position: 'relative' }}>
                                        <i className="fas fa-search country-search-icon"></i>
                                        <input
                                            type="text"
                                            className="country-search-input"
                                            placeholder="Search countries by name or code..."
                                            value={countrySearch}
                                            onChange={(e) => setCountrySearch(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="countries-list">
                                    {filteredCountries.length > 0 ? (
                                        filteredCountries.map(country => {
                                            const isSelected = countryModalMode === 'create'
                                                ? createForm.country_codes?.includes(country.code)
                                                : editForm.country_codes?.includes(country.code);

                                            return (
                                                <label
                                                    key={country.code}
                                                    className={isSelected ? 'selected' : ''}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            if (countryModalMode === 'create') {
                                                                handleCreateCountryToggle(country.code);
                                                            } else {
                                                                handleCountryToggle(country.code);
                                                            }
                                                        }}
                                                    />
                                                    <span>{country.name}</span>
                                                    <span className="country-code">{country.code}</span>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <div className="no-countries-found">
                                            <i className="fas fa-search"></i>
                                            <p>No countries found</p>
                                        </div>
                                    )}
                                </div>

                                <div className="add-country-section">
                                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#475569', fontSize: '0.95rem' }}>
                                        <i className="fas fa-plus-circle"></i> Add Custom Country
                                    </h4>
                                    <div className="add-country-form">
                                        <input
                                            type="text"
                                            className="add-country-input"
                                            placeholder="Country Code (e.g., XX)"
                                            value={newCountryCode}
                                            onChange={(e) => setNewCountryCode(e.target.value.toUpperCase())}
                                            maxLength={2}
                                            style={{ flex: '0 0 120px' }}
                                        />
                                        <input
                                            type="text"
                                            className="add-country-input"
                                            placeholder="Country Name"
                                            value={newCountryName}
                                            onChange={(e) => setNewCountryName(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="add-country-btn"
                                            onClick={handleAddCustomCountry}
                                            disabled={!newCountryCode || !newCountryName}
                                        >
                                            <i className="fas fa-plus"></i>
                                            Add
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="country-modal-footer">
                                <span className="selected-count">
                                    {countryModalMode === 'create'
                                        ? `${createForm.country_codes?.length || 0} countries selected`
                                        : `${editForm.country_codes?.length || 0} countries selected`
                                    }
                                </span>
                                <div className="modal-actions">
                                    <button className="btn btn-secondary" onClick={closeCountryModal}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-primary" onClick={closeCountryModal}>
                                        <i className="fas fa-check"></i>
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentGateways;
